import axios, { AxiosError } from 'axios';
import { getCookie, setCookie } from 'cookies-next';

import { AuthTokenError } from './errors/AuthTokenError';

interface AxiosErrorResponse {
  code?: string;
}

type FailedRequestQueue = {
  onSuccess: (token: string) => void;
  onFailure: (error: AxiosError) => void;
};

let isRefreshing = false;
let failedRequestsQueue: FailedRequestQueue[] = [];

function setupClient() {
  const token = getCookie('@event-monitor-web:token-1.0.1');

  const api = axios.create({
    baseURL: import.meta.env.VITE_SSO_BASE_URL,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  api.interceptors.response.use(
    (response) => {
      return response;
    },
    (error: AxiosError<AxiosErrorResponse>) => {
      if (error.response?.status === 401) {
        if (
          error.response.data?.code === 'token.expired' ||
          error.response.data?.code === 'token.invalid'
        ) {
          const refreshToken = getCookie(
            '@event-monitor-web:refreshToken-1.0.1',
          );

          const originalConfig = error.config;

          if (!isRefreshing) {
            isRefreshing = true;

            api
              .post('/session/refresh', {
                refresh_token: refreshToken,
              })
              .then((response) => {
                const { token, refresh_token } = response.data;

                setCookie('@event-monitor-web:token-1.0.1', token, {
                  maxAge: 60 * 60 * 24 * 30,
                  path: '/',
                });

                setCookie(
                  '@event-monitor-web:refreshToken-1.0.1',
                  refresh_token,
                  {
                    maxAge: 60 * 60 * 24 * 30,
                    path: '/',
                  },
                );

                api.defaults.headers.Authorization = `Bearer ${token}`;

                failedRequestsQueue.forEach((request) =>
                  request.onSuccess(token),
                );
                failedRequestsQueue = [];
              })
              .catch((err) => {
                failedRequestsQueue.forEach((request) =>
                  request.onFailure(err),
                );
                failedRequestsQueue = [];
              })
              .finally(() => {
                isRefreshing = false;
              });
          }

          return new Promise((resolve, reject) => {
            failedRequestsQueue.push({
              onSuccess: (token: string) => {
                if (!originalConfig?.headers) {
                  return;
                }

                originalConfig.headers.Authorization = `Bearer ${token}`;

                resolve(api(originalConfig));
              },
              onFailure: (err: AxiosError) => {
                reject(err);
              },
            });
          });
        } else {
          return Promise.reject(new AuthTokenError());
        }
      }

      return Promise.reject(error);
    },
  );

  return api;
}

export const apiSSO = setupClient();
